import { db, storage } from '../config/firebase';
import { collection, addDoc, serverTimestamp, getDocs, query, where, doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/**
 * Uploads an image file to Firebase Storage for a character.
 * @param {string} characterId - The ID of the character.
 * @param {File} file - The image file to upload.
 * @returns {Promise<string>} The download URL of the uploaded image.
 * @throws Will throw an error if the upload fails.
 */
export const uploadCharacterImage = async (characterId, file) => {
  if (!characterId || !file) {
    throw new Error("Character ID and file are required for image upload.");
  }
  // Sanitize file name or use a fixed name to prevent issues
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '');
  // imagePath updated to not use userId
  const imagePath = `characterImages/${characterId}/${sanitizedFileName || 'profileImage'}`; 
  const storageRef = ref(storage, imagePath);

  try {
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading character image:", error);
    throw new Error("Failed to upload character image.");
  }
};

/**
 * Creates a new character document in Firestore.
 * @param {object} characterData - Object containing character details.
 * @param {string} characterData.name - The character's name.
 * @param {string} characterData.userId - The UID of the user creating the character.
 * @param {string} characterData.race - The character's race.
 * @param {Array<{className: string, classLevel: number}>} characterData.classes - Array of character's classes and levels.
 * @param {number} characterData.level - The character's starting level. // This will be replaced by totalLevel derived from classes
 * @param {File} [characterData.imageFile] - Optional image file for the character.
 * @param {string} [characterData.dndBeyondLink] - Optional D&D Beyond character sheet link.
 * @returns {Promise<string>} The ID of the newly created character document.
 * @throws Will throw an error if characterData is invalid or Firestore operation fails.
 */
export const createCharacter = async (characterData) => {
  // Destructure classes, remove single 'class' and 'level', add dndBeyondLink
  const { name, userId, race, classes, imageFile, dndBeyondLink } = characterData;

  if (!name || !userId || !race || !Array.isArray(classes) || classes.length === 0) {
    const errorMessage = "Invalid character data: name, userId, race, and at least one class are required.";
    console.error(errorMessage, { name, userId, race, classes });
    throw new Error(errorMessage);
  }

  let totalLevel = 0;
  for (const cls of classes) {
    if (!cls.className || typeof cls.classLevel !== 'number' || cls.classLevel < 1) {
      const errorMessage = "Invalid class entry in classes array: each class must have a className and a level of at least 1.";
      console.error(errorMessage, cls);
      throw new Error(errorMessage);
    }
    totalLevel += cls.classLevel;
  }

  if (totalLevel < 1) { // Should not happen if individual class levels are >= 1 and classes array is not empty
    throw new Error("Total character level must be at least 1.");
  }
  try {
    const newCharacterData = {
      name,
      userId,
      race,
      classes, // Store the array of class objects
      totalLevel, // Store the calculated total level
      dndBeyondLink: dndBeyondLink || null, // Store D&D Beyond link if provided
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      imageUrl: null, 
    };

    const docRef = await addDoc(collection(db, "characters"), newCharacterData);
    const characterId = docRef.id;

    if (imageFile && characterId) {
      try {
        // Call to uploadCharacterImage updated: userId removed
        const imageUrl = await uploadCharacterImage(characterId, imageFile); 
        await updateDoc(doc(db, "characters", characterId), { imageUrl });
      } catch (uploadError) {
        // Log the error but don't let it block character creation if only image upload fails
        console.error("Failed to upload character image during creation, but character created:", uploadError);
        // Optionally, you could set a specific field in Firestore to indicate image upload failure
      }
    }
    return characterId;
  } catch (error) {
    console.error("Error creating character in Firestore:", error);
    throw new Error("Failed to create character. Please try again."); // Generic error for UI
  }
};

/**
 * Fetches all characters for a given user ID from Firestore.
 * @param {string} userId - The UID of the user.
 * @returns {Promise<Array<object>>} An array of character objects, each including its Firestore ID.
 * @throws Will throw an error if userId is not provided or Firestore operation fails.
 */
export const getCharactersByUserId = async (userId) => {
  if (!userId) {
    const errorMessage = "User ID is required to fetch characters.";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
  try {
    const charactersRef = collection(db, "characters");
    // Query for characters where the userId field matches the provided userId
    const q = query(charactersRef, where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    const characters = [];
    querySnapshot.forEach((doc) => {
      characters.push({ id: doc.id, ...doc.data() });
    });
    return characters;
  } catch (error) {
    console.error("Error fetching characters by user ID from Firestore:", error);
    throw new Error("Failed to fetch characters. Please try again.");
  }
};

/**
 * Fetches race options from a single document in Firestore.
 * Assumes a 'races' collection contains a document named 'races'
 * which in turn has an array field named 'races' containing the race strings.
 * @returns {Promise<Array<string>>} An array of race name strings.
 * @throws Will throw an error if Firestore operation fails or data is not in expected format.
 */
export const getRaceOptions = async () => {
  try {
    const docRef = doc(db, "races", "races"); // Document ID is also 'races'
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Ensure the 'races' field exists and is an array
      if (data && Array.isArray(data.races)) {
        // Filter out any non-string values just in case and sort
        const raceOptions = data.races.filter(race => typeof race === 'string').sort();
        if (raceOptions.length === 0) {
          console.warn("Race options array is empty in document 'races/races'.");
        }
        return raceOptions;
      } else {
        console.error("Field 'races' is missing or not an array in document 'races/races'.");
        throw new Error("Race data is not in the expected format.");
      }
    } else {
      console.error("Document 'races/races' does not exist.");
      throw new Error("Race configuration document not found.");
    }
  } catch (error) {
    console.error("Error fetching race options from Firestore:", error);
    // Re-throw a more user-friendly error or the original one
    throw new Error(error.message || "Failed to load race options. Please try again.");
  }
};

/**
 * Fetches class options from a single document in Firestore.
 * Assumes a 'classes' collection contains a document named 'classes'
 * which in turn has an array field named 'classes' containing the class strings.
 * @returns {Promise<Array<string>>} An array of class name strings.
 * @throws Will throw an error if Firestore operation fails or data is not in expected format.
 */
export const getClassOptions = async () => {
  try {
    const docRef = doc(db, "classes", "classes"); // Document ID is also 'classes'
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Ensure the 'classes' field exists and is an array
      if (data && Array.isArray(data.classes)) {
        // Filter out any non-string values just in case and sort
        const classOptions = data.classes.filter(cls => typeof cls === 'string').sort();
        if (classOptions.length === 0) {
          console.warn("Class options array is empty in document 'classes/classes'.");
        }
        return classOptions;
      } else {
        console.error("Field 'classes' is missing or not an array in document 'classes/classes'.");
        throw new Error("Class data is not in the expected format.");
      }
    } else {
      console.error("Document 'classes/classes' does not exist.");
      throw new Error("Class configuration document not found.");
    }
  } catch (error) {
    console.error("Error fetching class options from Firestore:", error);
    // Re-throw a more user-friendly error or the original one
    throw new Error(error.message || "Failed to load class options. Please try again.");
  }
};

/**
 * Fetches a single character by its ID from Firestore.
 * @param {string} characterId - The ID of the character to fetch.
 * @returns {Promise<object|null>} The character object if found, otherwise null.
 * @throws Will throw an error if characterId is not provided or Firestore operation fails.
 */
export const getCharacterById = async (characterId) => {
  if (!characterId) {
    const errorMessage = "Character ID is required to fetch a character.";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
  try {
    const charRef = doc(db, "characters", characterId);
    const docSnap = await getDoc(charRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    console.warn(`Character with ID ${characterId} not found.`);
    return null;
  } catch (error) {
    console.error(`Error fetching character by ID ${characterId} from Firestore:`, error);
    throw new Error("Failed to fetch character details. Please try again.");
  }
};

/**
 * Updates an existing character document in Firestore.
 * @param {string} characterId - The ID of the character to update.
 * @param {object} dataToUpdate - Object containing the fields to update.
 * @returns {Promise<void>}
 * @throws Will throw an error if characterId or dataToUpdate is invalid, or Firestore operation fails.
 */
export const updateCharacter = async (characterId, dataToUpdate) => {
  if (!characterId || !dataToUpdate || Object.keys(dataToUpdate).length === 0) {
    const errorMessage = "Character ID and data are required for update.";
    console.error(errorMessage, { characterId, dataToUpdate });
    throw new Error(errorMessage);
  }

  try {
    // Ensure 'updatedAt' is always set
    const finalDataToUpdate = { ...dataToUpdate, updatedAt: serverTimestamp() };

    // If classes are being updated, recalculate totalLevel
    if (finalDataToUpdate.classes && Array.isArray(finalDataToUpdate.classes)) {
      let totalLevel = 0;
      if (finalDataToUpdate.classes.length > 0) {
        for (const cls of finalDataToUpdate.classes) {
          if (!cls.className || typeof cls.classLevel !== 'number' || cls.classLevel < 1) {
            // This indicates an attempt to set invalid class data.
            // Depending on desired strictness, could throw an error or log a warning.
            // For now, throwing an error to ensure data integrity.
            const errMsg = "Invalid class entry in classes array during update: each class must have a className and a level of at least 1.";
            console.error(errMsg, cls);
            throw new Error(errMsg);
          }
          totalLevel += cls.classLevel;
        }
        if (totalLevel < 1) { // Should be caught by the loop's cls.classLevel < 1 check if classes is not empty
             throw new Error("Total character level must be at least 1 when updating classes.");
        }
      } else {
        // If classes array is explicitly set to empty, this implies level 0.
        // This might be an undesirable state, consider disallowing.
        // For now, we'll allow it, but it means totalLevel will be 0.
        // Or, enforce that classes array cannot be empty if it's part of an update.
        // Let's enforce that it cannot be set to empty if it's being modified.
        // If the intention is to remove all classes, that's a character deletion, not update.
        // However, if a character must have at least one class, this path should error.
        // For simplicity, if `classes` is an empty array, `totalLevel` will be 0.
        // A better approach might be to prevent `classes` from being an empty array if it's part of an update.
        // Let's assume if `classes` is provided, it must result in a valid character state.
        // Re-evaluating: if classes is empty, totalLevel is 0. This is probably bad.
        // Let's require at least one class if `classes` field is present in the update.
         throw new Error("Classes array cannot be empty if provided for update. Character must have at least one class.");
      }
      finalDataToUpdate.totalLevel = totalLevel;
    } else if (finalDataToUpdate.hasOwnProperty('classes') && !Array.isArray(finalDataToUpdate.classes)) {
        // If 'classes' is provided but not an array (e.g. null or other type)
        throw new Error("If 'classes' field is provided for update, it must be an array.");
    }


    const charRef = doc(db, "characters", characterId);
    await updateDoc(charRef, finalDataToUpdate);
  } catch (error) {
    console.error(`Error updating character ${characterId} in Firestore:`, error);
    throw new Error("Failed to update character. Please try again.");
  }
};

/**
 * Fetches all characters from Firestore.
 * @returns {Promise<Array<object>>} An array of all character objects, each including its Firestore ID.
 * @throws Will throw an error if Firestore operation fails.
 */
export const getAllCharacters = async () => {
  try {
    const charactersRef = collection(db, "characters");
    const querySnapshot = await getDocs(charactersRef);
    const characters = [];
    querySnapshot.forEach((doc) => {
      characters.push({ id: doc.id, ...doc.data() });
    });
    return characters;
  } catch (error) {
    console.error("Error fetching all characters from Firestore:", error);
    throw new Error("Failed to fetch characters. Please try again.");
  }
};

/**
 * Uploads an image file to Firebase Storage for accolades.
 * @param {File} file - The image file to upload.
 * @param {string} category - The category folder (e.g., 'accolades').
 * @returns {Promise<string>} The download URL of the uploaded image.
 * @throws Will throw an error if the upload fails.
 */
export const uploadImage = async (file, category = 'images') => {
  if (!file) {
    throw new Error("File is required for image upload.");
  }
  
  try {
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storageRef = ref(storage, `${category}/${fileName}`);
    
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error("Error uploading image:", error);
    throw new Error("Failed to upload image.");
  }
};
